import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { useTheme as useWidgetTheme } from "styled-components";

import { useSelector } from "react-redux";
import {
  StyledBotContainer,
  StyledFloatingButtonWrap,
} from "../Styles/StyledScreens";

import { HostWindowCommService } from "../../Services/IframeHostService/HostWindowCommService";
import { IEvents, OEvents } from "../../Services/IframeHostService/models";
import { useAppDispatch, useAppSelector } from "../../Store";
import { useBotConfigStore } from "../../Store/Slices/ChatbotSlices/BotConfigSlice";
import {
  botInfoStore,
  setIsOpenedFirstTime,
  setOpenStatus,
  setBotToFullScreen,
  setBotToMobileView,
} from "../../Store/Slices/socket/BotInfoSlice";
import {
  deviceSizeStore,
  setDeviceSize,
} from "../../Store/Slices/socket/DeviceSizeSlice";

import MainUI from "../../socket/screens/MainUI";
import useSocket from "../hooks/useSocket";
import { PreviewType } from "../utils/Enum";
import CalloutMessageScreen from "./CalloutMessageScreen";
import SpeedDialButton from "./SpeedDialButton";
import {
  setExecuteConnectSocketEventWithConnectUser,
  setExecuteConnectSocketEventWithReConnectUser,
  socketStatusStore
} from "../../Store/Slices/socket/SocketSlice";
import useTimer from "../hooks/useTimer";
import {getWidgetPositionConfig } from "../utils/utils";
import {
  CUSTOM_HEIGHT_BOUNDS,
  CUSTOM_WIDTH_BOUNDS,
  clampCustomSize,
  Surface,
  WIDGET_SIZE_PRESETS,
} from "../../Models/ChatbotConfiguration";

interface Props { }

const Home: React.FC<Props> = () => {
  const containerRef = useRef<any>(null);
  const floatingButtonRef = useRef<any>(null);
  const autoOpenTimerRef = useRef<NodeJS.Timeout | null>(null);
  const dispatch = useAppDispatch();
  const { timer } = useTimer(1500);
  const { botConfig } = useAppSelector(useBotConfigStore);
  const IframeDimensions = useSelector(deviceSizeStore);
  const [mobileView, setMobileView]= useState<boolean>(false)
  const { chatOpened, isOpenedFirstTime, widgetPreviewType, isBotSetToFullScreen , isBotSetToMobileView, isUserExpanded} = useSelector(botInfoStore);
  const [fullscreen, setFullscreen] = React.useState<boolean>(false);

  //* do not remove this import
  const { socket } = useAppSelector(socketStatusStore);
  const { height, width } =
    useSelector(deviceSizeStore);

  const [clientViewPortSize, setClientViewPortSize] = React.useState<any>(0);

  let commService = useMemo(() => {
    return HostWindowCommService.getInstance();
  }, []);

  const chatbotAutoOpen = useMemo(
    () => botConfig?.config?.always_open ?? botConfig?.config?.auto_open ?? false,
    [botConfig]
  );

  const chatWidgetAutoOpen = useMemo(
    () => botConfig?.config?.auto_open_enabled ?? false,
    [botConfig]
  );

  const chatbotAutoOpenDelay = useMemo(() => {
    const delayInSeconds = botConfig?.config?.auto_open_delay ?? 8; // Default 8 seconds
    return delayInSeconds * 1000; // Convert seconds to milliseconds
  }, [botConfig]);

  // Resolved once in WidgetApp: workspace on the rollout AND the bot's own
  // "Enable Modern Style" on. Read from the theme so sizing stays in step with
  // the styling rather than deciding it a second way. Without this gate every
  // existing widget would grow from 376x610, since geta-core defaults
  // widget_size to "standard" for every config.
  const widgetTheme: any = useWidgetTheme();
  const isNewUi = !!widgetTheme?.industry_style?.enabled;

  const widgetSizePreset = useMemo(() => {
    if (!isNewUi) return null;
    const size = (botConfig?.config?.widget_size ?? "standard") as keyof typeof WIDGET_SIZE_PRESETS;
    return WIDGET_SIZE_PRESETS[size] ?? WIDGET_SIZE_PRESETS.standard;
  }, [botConfig, isNewUi]);

  // Master applied no bounds to these; only the redesign clamps them.
  const resolveCustomSize = (value: unknown, bounds: { min: number; max: number }) =>
    isNewUi ? clampCustomSize(value, bounds) : (Number(value) || null);

  const effectiveSurface: Surface = isUserExpanded ? "expanded" : "docked";

  // Resize event listener
  useEffect(() => {
    const handleParentWindowResize = (event: any) => {
      const viewportSize = event?.data?.data?.viewportSize;
      setClientViewPortSize(event?.data?.data?.viewportSize);

      let newHeight;

      if (viewportSize?.width < 768) {
        newHeight = viewportSize?.height;
      } else if (effectiveSurface === "expanded") {
        newHeight = viewportSize?.height;
      } else {
        if (viewportSize?.height < 500) {
          newHeight = viewportSize?.height;
        } else {
          newHeight =
            viewportSize?.height > 860 ? 751 : viewportSize?.height - 100
        }

        // newHeight = viewportSize?.height > 860 ? 751 : viewportSize?.height - 100;
      }

      let requestedWidth;
      if (!isNewUi) {
        // Exactly what master asked for — no custom-width branch, no preset.
        requestedWidth = viewportSize?.width > 768 ? 376 : viewportSize?.width;
      } else if (viewportSize?.width < 768) {
        requestedWidth = viewportSize?.width;
      } else if (effectiveSurface === "expanded") {
        requestedWidth = viewportSize?.width;
      } else if (resolveCustomSize(botConfig?.config?.custom_width, CUSTOM_WIDTH_BOUNDS)) {
        requestedWidth = Math.min(
          (resolveCustomSize(botConfig?.config?.custom_width, CUSTOM_WIDTH_BOUNDS) as number) + 32,
          viewportSize?.width
        );
      } else {
        requestedWidth = (widgetSizePreset?.width ?? 344) + 32;
      }

      let temp = {
        width: requestedWidth,
        height: newHeight,
        // Presets are sized deliberately — don't let the legacy 376 cap undo them.
        usePresetWidth: isNewUi && viewportSize?.width >= 768,
      };

      dispatch(setDeviceSize(temp));
    };

    // Register the event listener when the component mounts
    commService.registerEventListener(
      IEvents.PARENT_WINDOW_RESIZE,
      handleParentWindowResize
    );
  });

  const handleQuickChat = () => {
    commService.postMessage({
      event_type: OEvents.CHATBOT_OPEN,
      data: { open: chatOpened ? true : false },
    });
    dispatch(setOpenStatus(!chatOpened));

    if (!isOpenedFirstTime) {
      dispatch(setIsOpenedFirstTime(true));
      // Clear auto-open timer when user manually opens the chatbot
      if (autoOpenTimerRef.current) {
        clearTimeout(autoOpenTimerRef.current);
        autoOpenTimerRef.current = null;
      }
    }
  };

  const frameWidth = useMemo(() => {
    if (effectiveSurface === "expanded") {
      return clientViewPortSize?.width || width;
    }
    return resolveCustomSize(botConfig?.config?.custom_width, CUSTOM_WIDTH_BOUNDS) ?? width
   }, [width,botConfig,effectiveSurface,clientViewPortSize]);

  const frameHeight = useMemo(() => {
    const autoHeight = botConfig?.config?.auto_height ?? false;
    const modifiedFinalValue = height - (botConfig?.config?.auto_height_limiting_number ?? 0);

    if (effectiveSurface === "expanded") {
      return clientViewPortSize?.height || height;
    }

    // If window height is less than 620px, make frameHeight equal to window height
    if (height <= 620) {
      return height;
    }

    if (!isNewUi) {
      // Master had no custom-height branch and a fixed 610 ceiling.
      const legacyMax = autoHeight ? modifiedFinalValue : 610;
      return legacyMax ?? 610;
    }

    const customHeight = resolveCustomSize(
      botConfig?.config?.custom_height,
      CUSTOM_HEIGHT_BOUNDS
    );
    if (customHeight) {
      return customHeight;
    }

    const presetHeight = widgetSizePreset ? widgetSizePreset.height + 78 : 610;
    const maxHeight = autoHeight ? modifiedFinalValue : presetHeight;
    return maxHeight ?? 610;
   }, [height,botConfig,widgetSizePreset,effectiveSurface,clientViewPortSize]);

  const handleOpenStateIframeDimensions = () => {
    const wh2 = {
      width: frameWidth - 16,
      height: frameHeight - 8,
      state: "open",
    };
    commService.postMessage({
      event_type: OEvents.CHATBOT_RESIZE,
      data: wh2,
    });
  };

  useEffect(() => {
    if (chatOpened) {
      handleOpenStateIframeDimensions();
    }
  }, [clientViewPortSize, chatOpened, effectiveSurface]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClosedStateIframeDimensions = () => {
    if (floatingButtonRef?.current) {
      let { width: closedDivWidth, height: closedDivHeight } =
        floatingButtonRef.current.getBoundingClientRect();

      let newCalculatedWidth = closedDivWidth + 16 > 80 ? floatingButtonRef.current.offsetWidth : 80;
      let newCalculatedHeight =
        closedDivHeight + 16 > 84 ? closedDivHeight : 80;

      setTimeout(() => {
        const wh = {
          width: newCalculatedWidth,
          height: newCalculatedHeight,
          state: "close",
        };
        commService.postMessage({
          event_type: OEvents.CHATBOT_RESIZE,
          data: wh,
        });
      }, 100);
    }
  };

  useEffect(() => {
    if (chatOpened && !isOpenedFirstTime) {
      dispatch(setIsOpenedFirstTime(true));
    }
    if (chatOpened) {
      handleOpenStateIframeDimensions();
    } else {
      handleClosedStateIframeDimensions();
    }
  }, [chatOpened, isOpenedFirstTime]); // eslint-disable-line react-hooks/exhaustive-deps

  const { disconnectSocketEvent, connectSocketEventWithReConnectUser, connectSocketEventWithConnectUser } = useSocket();

  useEffect(() => {
    // connectSocketEvent();
    dispatch(setExecuteConnectSocketEventWithReConnectUser(connectSocketEventWithReConnectUser));
    //@ts-ignore
    dispatch(setExecuteConnectSocketEventWithConnectUser(connectSocketEventWithConnectUser));
    return () => {
      disconnectSocketEvent();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (
      widgetPreviewType === PreviewType.getaWidgetPreview || chatbotAutoOpen
    ) {
      setTimeout(() => {
        dispatch(setOpenStatus(true));   
        
      }, 100); // DO NOT remove settimeout //
    }
  }, [widgetPreviewType]); // eslint-disable-line react-hooks/exhaustive-deps


  // Seperate useEffect for auto open delay because it affects the widget preview type
  useEffect(() => {
    if (chatWidgetAutoOpen && !isOpenedFirstTime) {
      autoOpenTimerRef.current = setTimeout(() => {
        dispatch(setOpenStatus(true));
        autoOpenTimerRef.current = null; // Clear the reference after timer fires
      }, chatbotAutoOpenDelay); // DO NOT remove settimeout //

    }

    // Cleanup function to clear timer if component unmounts or dependencies change
    return () => {
      if (autoOpenTimerRef.current) {
        clearTimeout(autoOpenTimerRef.current);
        autoOpenTimerRef.current = null;
      }
    };
  }, [chatWidgetAutoOpen, isOpenedFirstTime, chatbotAutoOpenDelay]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isBotSetToFullScreen) {
      setTimeout(() => {
        dispatch(setBotToFullScreen(true));
        dispatch(setOpenStatus(true));
        setFullscreen(true);
      }, 100);
    }
  }, [isBotSetToFullScreen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(()=>{
    if(isBotSetToMobileView){
      setTimeout(() => {
        dispatch(setBotToMobileView(true));
        dispatch(setOpenStatus(true));
        setMobileView(true);
      }, 100);
    }
  },[isBotSetToMobileView])

  const widgetPositionConfig = useMemo(() => {
    return getWidgetPositionConfig({config:botConfig.config, IframeDimensions:IframeDimensions});
  }, [IframeDimensions, botConfig]);

  if (timer > 0) return null;

  return (
    <StyledBotContainer
      devicewidth={frameWidth}
      isFullScreen={fullscreen}
      deviceheight={frameHeight}
      chatopened={chatOpened ? 1 : 0}
      id="bot-container"
      ref={containerRef}
      WIDGET_POSITION={widgetPositionConfig}
      isTooltipVisible={!isOpenedFirstTime}
      surface={effectiveSurface}
    >
      <MainUI
        isFullScreen={fullscreen}
        isMobileView={mobileView}
        surface={effectiveSurface}
      />

      <StyledFloatingButtonWrap
        chatopened={chatOpened ? 1 : 0}
        tooltipPosition={botConfig.config?.tooltip?.position}
        WIDGET_POSITION={widgetPositionConfig}
        isTooltipVisible={isOpenedFirstTime}
        ref={floatingButtonRef}
        hideFloatingButton={botConfig.config?.hide_floating_Button ?? false}
      >
        {botConfig.config?.tooltip?.enabled &&
          !chatOpened &&
          !isOpenedFirstTime ? (
          <CalloutMessageScreen
            handleQuickChat={handleQuickChat}
            handleClosedStateIframeDimensions={
              handleClosedStateIframeDimensions
            }
          />
        ) : null}
        <SpeedDialButton handleQuickChat={handleQuickChat} />
      </StyledFloatingButtonWrap>
    </StyledBotContainer>
  );
};

export default memo(Home);
